/**
 * What the workspace switch does (lifted out of src/components/WorkspaceTransitionOverlay.tsx unchanged, PV5-8:D part 1,
 * 2026-10-13). Today's overlay draws what these answer, and the new design's draws the same answers (part 2), so the two
 * cannot announce, hold, release or seal differently.
 *
 * - useWorkspaceSwitchView: WHAT is announced and WHEN. A live switch begun through beginWorkspaceTransition (any switch
 *   path: a foreign thread opened, the workspace pill, the login card, the organization page, onboarding's launch) is
 *   'switching', or 'error' when it failed. After the hard navigation the boot continuation re-raises the same overlay
 *   from the sessionStorage marker as 'arriving', and holds it until the destination workspace RESOLVES (or settles
 *   with no account, or the watchdog fires): then a short beat, a fade (`leaving`), and the marker is consumed. Every
 *   timer lives in one ledger, so an unmount never leaks one. Mounted once, at app level (App.tsx).
 * - useSwitchSeal: nothing behind the overlay is reachable while it is up. The page's scroll is locked (reference
 *   counted), focus moves to the overlay's own sink (the element its ref is given to), and every key is swallowed in the
 *   capture phase at the document. There is no dismiss: the overlay ends only by navigation, resolution or its timers
 *   (owner directive 2026-08-19, "not allowing the user to touch anything else").
 */
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useBodyScrollLock } from '../useBodyScrollLock';
import {
  BOOT_CONTINUATION_MAX_MS,
  BOOT_RELEASE_BEAT_MS,
  BOOT_RELEASE_FADE_MS,
  clearBootTransition,
  getWorkspaceTransitionState,
  readBootTransition,
  subscribeWorkspaceTransition,
  type WorkspaceTransitionState,
} from '../../services/workspaceTransition';
import {
  getActiveWorkspaceAccount,
  onActiveWorkspaceResolved,
  onWorkspaceSettledWithoutAccount,
} from '../../services/activeWorkspace';

/** How long the boot continuation fades for once released: the drawing animates exactly what the hook waits for. */
export const SWITCH_FADE_MS = BOOT_RELEASE_FADE_MS;

/** Everything the overlay needs to render one announcement. */
export interface WorkspaceSwitchView {
  kind: 'switching' | 'error' | 'arriving';
  targetAccountId: string;
  label: string | null;
  threadTitle?: string;
}

/** The announcement on screen, how it is leaving, and where it is drawn. */
export interface WorkspaceSwitchShown {
  view: WorkspaceSwitchView;
  /** The boot continuation is fading out (a live switch is never faded here). */
  leaving: boolean;
  /** The overlay is portaled to <body>, above everything (BODY_PORTAL_Z_INDEX). */
  portalTarget: HTMLElement;
}

/** The announcement to show, or null when there is none (or no document to show it in). */
export function useWorkspaceSwitchView(): WorkspaceSwitchShown | null {
  const [live, setLive] = useState<WorkspaceTransitionState | null>(
    () => getWorkspaceTransitionState(),
  );
  const [boot, setBoot] = useState<{ targetAccountId: string; label: string | null } | null>(() => {
    const marker = readBootTransition();
    return marker ? { targetAccountId: marker.targetAccountId, label: marker.label } : null;
  });
  const [bootLeaving, setBootLeaving] = useState(false);

  useEffect(() => subscribeWorkspaceTransition(setLive), []);

  // Boot continuation lifecycle: hold until the destination workspace
  // RESOLVES (or settles with no account, or the watchdog fires), then a
  // short beat, a fade, release + consume the marker. Every timer lives in
  // one ledger so unmount can never leak one (the pre-2026-08-20 release
  // timer leaked).
  useEffect(() => {
    if (!boot) return;
    // A re-run of this effect (dev-mode double-invoke, or a future boot
    // value) must start from a fully visible overlay — a half-finished
    // fade from a previous run may have left the leaving flag set.
    setBootLeaving(false);
    let released = false;
    const timers = new Set<number>();
    const schedule = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    const finalize = () => {
      clearBootTransition();
      setBoot(null);
      setBootLeaving(false);
    };
    const release = (beatMs: number) => {
      if (released) return;
      released = true;
      schedule(() => {
        setBootLeaving(true);
        schedule(finalize, BOOT_RELEASE_FADE_MS);
      }, beatMs);
    };

    // Already resolved before we mounted (fast boot): the standard beat on a
    // match; a MISMATCH releases immediately — holding while announcing the
    // wrong org would be the exact trust break this overlay prevents.
    const already = getActiveWorkspaceAccount();
    if (already) {
      release(already === boot.targetAccountId ? BOOT_RELEASE_BEAT_MS : 0);
      return () => {
        for (const id of Array.from(timers)) window.clearTimeout(id);
      };
    }

    const unsubscribeResolved = onActiveWorkspaceResolved(() => {
      // Resolved — whatever org it is, the surfaces are now keyed; the beat
      // lets the first correctly-keyed frame land under the overlay.
      release(BOOT_RELEASE_BEAT_MS);
    });
    // Settled with NO workspace (guest / unmigrated): nothing will ever be
    // keyed — release now instead of riding out the watchdog (the 12s
    // signed-out hang, 2026-08-20).
    const unsubscribeSettled = onWorkspaceSettledWithoutAccount(() => release(0));
    schedule(() => release(0), BOOT_CONTINUATION_MAX_MS);
    return () => {
      unsubscribeResolved();
      unsubscribeSettled();
      for (const id of Array.from(timers)) window.clearTimeout(id);
    };
  }, [boot]);

  if (typeof document === 'undefined') return null;

  const view: WorkspaceSwitchView | null = live
    ? {
        kind: live.phase === 'error' ? 'error' : 'switching',
        targetAccountId: live.request.targetAccountId,
        label: live.label,
        threadTitle: live.request.threadTitle,
      }
    : boot
      ? { kind: 'arriving', targetAccountId: boot.targetAccountId, label: boot.label }
      : null;

  if (!view) return null;
  return { view, leaving: !live && bootLeaving, portalTarget: document.body };
}

/**
 * Seal the page behind the overlay while it is drawn: the scroll lock, focus on the returned sink (give it to the
 * overlay's own element, with tabIndex -1), and every key swallowed. Call it from the component that draws the overlay,
 * so the seal lasts exactly as long as the overlay does.
 */
export function useSwitchSeal(): RefObject<HTMLDivElement> {
  const sinkRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(true);

  // Focus sink + total key swallow: nothing behind this overlay is
  // reachable by keyboard while it is up. Capture phase, document level.
  useEffect(() => {
    sinkRef.current?.focus();
    const swallow = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('keydown', swallow, true);
    return () => document.removeEventListener('keydown', swallow, true);
  }, []);

  return sinkRef;
}
